import { useState, type FormEvent } from 'react'
import { Link } from 'react-router-dom'
import { AuthLayout } from '@/components/AuthLayout'
import { supabase } from '@/lib/supabaseClient'
import { Telemetry } from '@/lib/telemetry'

/**
 * Request a password reset email. Supabase handles the actual email
 * sending; after the user clicks the link they land on /auth/new-password
 * (the redirectTo below) where UpdatePasswordPage takes over.
 *
 * This is a fire-and-forget screen. We always show the same success
 * state regardless of whether the email is registered, for the same
 * enumeration-resistance reasons as the signup flow.
 */
export function ResetPasswordPage() {
  const [email, setEmail] = useState('')
  const [loading, setLoading] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [error, setError] = useState('')

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    setLoading(true)
    setError('')

    // A2F-625: redirectTo must be present in the Supabase Redirect URLs
    // allowlist (prod + staging) or Supabase silently falls back to Site
    // URL — and Site URL routes commons users to the main app. See
    // docs/ctdcommons-setup.md §3 for the full allowlist + rationale.
    const { error: err } = await supabase.auth.resetPasswordForEmail(email.trim(), {
      redirectTo: `${window.location.origin}/auth/new-password`,
    })

    setLoading(false)
    if (err) {
      // Network / availability errors only — Supabase does not return
      // "no such user," so any error here is genuine.
      setError('Could not send reset email. Please try again later.')
      Telemetry.errorSwallowed({
        error_type: 'password_reset_failure',
        operation: 'password_reset_request',
        screen_name: '/auth/reset-password',
        error_message: err.message ?? 'unknown',
      })
      return
    }
    Telemetry.passwordResetRequested()
    setSubmitted(true)
  }

  if (submitted) {
    return (
      <AuthLayout
        title="Check your email"
        subtitle="If an account exists for that email, we sent a reset link."
      >
        <p className="text-sm text-slate-600 leading-relaxed">
          Click the link in the email to choose a new password. The link will
          expire in 1 hour.
        </p>
        <div className="mt-6 text-xs text-slate-500">
          <Link to="/" className="text-brand-orange hover:underline">
            &larr; Back to sign in
          </Link>
        </div>
      </AuthLayout>
    )
  }

  return (
    <AuthLayout
      title="Reset password"
      subtitle="Enter your email and we'll send you a reset link."
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label
            htmlFor="email"
            className="block text-xs font-medium text-slate-600 mb-1"
          >
            Email
          </label>
          <input
            id="email"
            type="email"
            required
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-brand-orange/30 focus:border-brand-orange"
          />
        </div>

        {error && (
          <div className="px-3 py-2 rounded-md bg-red-50 border border-red-200 text-xs text-red-700">
            {error}
          </div>
        )}

        <button
          type="submit"
          disabled={loading}
          className="w-full px-4 py-2 bg-brand-orange text-white text-sm font-medium rounded-md hover:bg-brand-orange/90 transition-colors disabled:opacity-50"
        >
          {loading ? 'Sending...' : 'Send reset link'}
        </button>
      </form>

      <div className="mt-6 text-center text-xs text-slate-500">
        <Link to="/" className="text-brand-orange hover:underline">
          &larr; Back to sign in
        </Link>
      </div>
    </AuthLayout>
  )
}
